import { gettext } from '@eventbrite/i18n';
import { fetchV3WithTranslateServerErrors } from './base';
import { translateArgumentErrors } from '@eventbrite/http';
import { unformat } from '@eventbrite/intl';
import { keysCamelToSnake } from '@eventbrite/transformation-utils';
import {
    SURVEY_ERROR_SPEC,
    translateDynamicSurveyErrors,
} from '../utils/surveys';
import { PAYMENT_METHODS, SQ_ANALYTICS_TOKEN } from '../constants';
import {
    CREDIT_CARD_NUMBER_FIELD,
    CARD_EXPIRATION_DATE_FIELD,
    CARD_SECURITY_CODE_FIELD,
    CARD_POSTAL_CODE_FIELD,
} from '../containers/checkout/constants';
import { _getPlacePayInUrl } from './constants';
import {
    PAYMENT_IDENTIFICATION_METHOD,
    PAYMENT_IDENTIFICATION_NUMBER,
} from '../components/paymentMethodForms/constants';
import {
    BUYER_EMAIL_FIELD,
    BUYER_FIRST_NAME_FIELD,
    BUYER_LAST_NAME_FIELD,
} from '../containers/checkout/constants';
import { isIdentificationRequiredForCurrency } from '../models/currency';

/**
 * How to handle errors on the pay in lifecycle
 */
const PAY_IN_ERROR_SPEC = {
    default: () =>
        gettext(
            'There was a problem submitting your pay in. Please try again.',
        ),

    INVALID_PAYMENT_REQUEST: () =>
        gettext(
            'There were errors with your payment. Please review your payment details',
        ),
    DECLINED: () => gettext('Your payment was declined'),
    PAYMENT_DECLINED: () => gettext('Your payment was declined'),
    ARGUMENTS_ERROR: translateArgumentErrors({
        default: translateDynamicSurveyErrors(SURVEY_ERROR_SPEC, false),
    }),
    CALL_PROCESSOR: () =>
        gettext(
            'We couldn’t process your payment because of an issue with your credit card. Contact your credit card company to resolve the issue.',
        ),
};

const _fetchV3WithPayInErrorHandling = (url, options = {}) =>
    fetchV3WithTranslateServerErrors(PAY_IN_ERROR_SPEC, url, options);

/**
 * Return a `fetch` Promise to get the available payment methods for a certain event
 *
 * @param {string} state
 */
export const getAvailablePaymentMethods = async (state) => {
    // TODO: This is currently hardcoded and should be changed to be coming from an API
    const { launchPayinsPaymentMethodPaypal = false } = state.app.featureFlags;
    const instrumentTypes = [
        {
            variants: ['MASTERCARD', 'VISA', 'AMEX'],
            type: 'CREDIT_CARD',
        },
    ];
    if (launchPayinsPaymentMethodPaypal === true) {
        instrumentTypes.push({
            variants: ['BRAINTREE_PAYPAL'],
            type: 'NONCE_BRAINTREE',
        });
    }

    return {
        response: {
            payment_options: [
                {
                    type: 'eventbrite',
                    instrument_types: instrumentTypes,
                },
            ],
        },
    };
};

const _payloadForNonceBraintree = ({ countryCode, payment, currency }) => {
    const billingInformation = _buildBillingInformation(
        countryCode,
        payment,
        currency,
    );
    return {
        payment_methods: [
            {
                payment_instrument_details: {
                    nonce: payment.paypalBraintreeNonce,
                    instrument_type: 'NONCE_BRAINTREE',
                },
                billing_information: billingInformation,
            },
        ],
    };
};

/**
 * Reformats expiration date input from MM/YY to MMYY,
 * how order service is expecting to receive it.
 */
const _reformatCardExpirationDate = (expirationDate) => {
    if (expirationDate && expirationDate.indexOf('/') >= 0) {
        let [month, year] = expirationDate.split('/');

        // Pad month with a leading 0 if it's only one digit. We allow
        // users to input M/YY but the backend always expects MM/YY.
        month = month.length === 1 ? `0${month}` : month;
        return [month, year].join('');
    }
    return undefined;
};

const _buildBillingInformation = (
    countryCode,
    {
        [CARD_POSTAL_CODE_FIELD]: postalCode,
        [PAYMENT_IDENTIFICATION_NUMBER]: number,
        [PAYMENT_IDENTIFICATION_METHOD]: identificationType,
        [BUYER_EMAIL_FIELD]: email,
        [BUYER_FIRST_NAME_FIELD]: firstName,
        [BUYER_LAST_NAME_FIELD]: lastName,
    },
    currency,
) => {
    const result = {
        address: keysCamelToSnake({
            postalCode,
            // XXX: countryCode is currently based on event venue, or TLD as
            // default. It should be changed to user billing information once
            // that data is collected
            country: countryCode,
        }),
        email,
        first_name: firstName,
        last_name: lastName,
    };

    if (isIdentificationRequiredForCurrency[currency]) {
        result.identification = keysCamelToSnake({
            number,
            identificationType,
        });
    }

    return result;
};

const currenciesWithExtraParams = ['BRL', 'ARS', 'MXN'];

export const formatCreditCardInstrument = ({
    countryCode,
    payment,
    currency,
    instrumentType,
}) => {
    let cardNumber;
    let cvv;
    let expirationDate;
    let analyticsToken;
    let billingInformation = {
        address: {},
    };
    const extraParams = {};

    if (payment) {
        cardNumber = payment[CREDIT_CARD_NUMBER_FIELD];
        cvv = payment[CARD_SECURITY_CODE_FIELD];
        expirationDate = _reformatCardExpirationDate(
            payment[CARD_EXPIRATION_DATE_FIELD],
        );
        billingInformation = _buildBillingInformation(
            countryCode,
            payment,
            currency,
        );
        analyticsToken = payment[SQ_ANALYTICS_TOKEN]
            ? payment[SQ_ANALYTICS_TOKEN]
            : null;
    }

    if (currenciesWithExtraParams.includes(currency)) {
        extraParams.cardholderName = payment.cardholder;
        if (payment.selectIssuer > 0) {
            extraParams.issuingBank = payment.selectIssuer;
            if (payment.installments) {
                extraParams.installments = payment.installments;
            }
        }
    }

    const instrument = keysCamelToSnake({
        instrumentType,
        cvv,
        cardNumber,
        expirationDate,
        analyticsToken,
        ...extraParams,
    });

    return {
        instrument,
        billingInformation,
    };
};

const _payloadForCredit = ({ countryCode, payment, currency }) => {
    const { instrument, billingInformation } = formatCreditCardInstrument({
        countryCode,
        payment,
        currency,
        instrumentType: PAYMENT_METHODS.CREDIT,
    });

    return {
        payment_methods: [
            {
                payment_instrument_details: instrument,
                billing_information: billingInformation,
            },
        ],
    };
};

const _payloadForUserInstrument = ({
    countryCode,
    payment,
    currency,
    userInstrument,
}) => {
    const billingInformation = _buildBillingInformation(
        countryCode,
        payment,
        currency,
    );

    return {
        payment_methods: [
            {
                payment_instrument_details: keysCamelToSnake(userInstrument),
                billing_information: billingInformation,
            },
        ],
    };
};

/**
 * Return the payload normally used to save payment methods
 *
 * @param {string} countryCode
 * @param {object} formData form data provided by checkout form
 * @param {string} currency
 */
export const getPayInPayload = (
    countryCode,
    { paymentMethod, payment, userInstrument },
    currency,
    currencyFormat,
) => {
    const payloadForPaymentMethod = {
        [PAYMENT_METHODS.CREDIT]: () =>
            _payloadForCredit({ countryCode, payment, currency }),
        [PAYMENT_METHODS.NONCE_BRAINTREE]: () =>
            _payloadForNonceBraintree({ countryCode, payment, currency }),
        [PAYMENT_METHODS.USER_INSTRUMENT]: () =>
            _payloadForUserInstrument({
                countryCode,
                payment,
                currency,
                userInstrument,
            }),
    };

    const paymentMethodBodyBuilder =
        payloadForPaymentMethod[paymentMethod] ||
        payloadForPaymentMethod[PAYMENT_METHODS.CREDIT];

    const floatAmount = unformat(payment.amount, currencyFormat);
    const integerAmountInCents = Math.round(Math.abs(floatAmount) * 100);
    const amount = `${currency}:${integerAmountInCents}`;

    return {
        ...paymentMethodBodyBuilder(),
        amount,
    };
};

/**
 * Return a `fetch` Promise to place the order
 *
 * @param {string} eventId event id to be placed
 */
export const placePayIn = (eventId, body = {}) =>
    _fetchV3WithPayInErrorHandling(_getPlacePayInUrl(eventId), {
        method: 'POST',
        body: JSON.stringify(body),
    });
